import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { apiFetch, apiGet } from '../lib/apiClient';
import { API_ROUTES } from '../lib/apiRoutes';

/**
 * Google Sign-In button using Google Identity Services (GIS).
 * Loads the GSI script, initializes with client ID from /config/public,
 * and sends the credential to /auth/google for JWT exchange.
 */
export default function GoogleSignInButton({ text = 'continue_with' }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch Google Client ID from backend config
  useEffect(() => {
    apiGet(API_ROUTES.public.config, { auth: false })
      .then(d => { if (d?.googleClientId) setClientId(d.googleClientId); })
      .catch(() => {});
  }, []);

  // Load Google GSI script + render button
  useEffect(() => {
    if (!clientId || !buttonRef.current) return;

    // Load GSI script if not already loaded
    if (!document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => initializeGoogle();
      document.head.appendChild(script);
    } else if (window.google?.accounts) {
      initializeGoogle();
    }

    function initializeGoogle() {
      if (!window.google?.accounts || !buttonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: false,
      });

      // Calculate parent width for full-width button
      const parentWidth = buttonRef.current.parentElement?.offsetWidth || 360;

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text,
        shape: 'rectangular',
        logo_alignment: 'center',
        width: Math.min(parentWidth, 400),  // Google caps at 400px
      });
    }
  }, [clientId]); // eslint-disable-line

  async function handleCredentialResponse(response) {
    if (!response?.credential) {
      toast.error('Google Sign-In failed');
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch(API_ROUTES.auth.google, {
        method: 'POST',
        body: { credential: response.credential },
        auth: false,
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Google login failed');
        return;
      }

      // Success — store JWT and redirect
      login(data.access_token, data.user, data.csrf_token);
      toast.success('Welcome!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Don't render if no client ID configured
  if (!clientId) return null;

  return (
    <div className="w-full">
      {loading && (
        <div className="flex items-center justify-center py-3">
          <div className="w-5 h-5 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin" />
        </div>
      )}
      <div
        ref={buttonRef}
        className={`flex items-center justify-center ${loading ? 'opacity-0 h-0 overflow-hidden' : ''}`}
      />
    </div>
  );
}
